import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../api';
import type { Campaign } from '../types';
import { ErrorNotice, Loading, formatNzDateTime } from '../components/Common';

export function PrintChecklistPage({ campaign }: { campaign: Campaign }) {
  const search = new URLSearchParams(location.search);
  const buildingId = search.get('buildingId') ?? '';
  const locations = useQuery({
    queryKey: ['locations', 'print', campaign.id, buildingId],
    enabled: Boolean(buildingId),
    queryFn: () =>
      apiClient.allLocations({
        campaignId: campaign.id,
        buildingId,
        status: 'all',
        active: 'all',
      }),
  });
  if (!buildingId) {
    return <ErrorNotice error={new Error('Choose a building before printing a checklist.')} />;
  }
  if (locations.isLoading) return <Loading label="Preparing checklist…" />;
  if (locations.error) return <ErrorNotice error={locations.error} />;
  const rows = locations.data?.data ?? [];
  const building = rows[0]?.building.name ?? 'Building';
  const campus = rows[0]?.campus.name ?? '';
  return (
    <main className="print-page" id="main-content">
      <div className="print-actions">
        <button className="button button-primary" type="button" onClick={() => window.print()}>
          Print checklist
        </button>
        <button className="button" type="button" onClick={() => window.close()}>
          Close
        </button>
      </div>
      <header>
        <img src="/icon.svg" width="56" height="56" alt="" />
        <div>
          <p>{campaign.publicLabel}</p>
          <h1>{building} distribution checklist</h1>
          <span>
            {campus} · Printed {formatNzDateTime(new Date().toISOString())}
          </span>
        </div>
      </header>
      <div className="print-warning">
        Never enter locked, restricted, staff-only, or unauthorized spaces. Mark the outcome and
        report it to a coordinator.
      </div>
      <table>
        <thead>
          <tr>
            <th scope="col">Done</th>
            <th scope="col">Room</th>
            <th scope="col">Type / capacity</th>
            <th scope="col">Permission</th>
            <th scope="col">Target</th>
            <th scope="col">Qty</th>
            <th scope="col">Outcome / note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((room) => (
            <tr key={room.id}>
              <td>☐</td>
              <td>
                <strong>{room.roomCode}</strong>
              </td>
              <td>
                {room.locationType}
                {room.capacity ? ` / ${room.capacity}` : ''}
              </td>
              <td>{room.permissionStatus.replaceAll('_', ' ')}</td>
              <td>{room.targetQuantity ?? room.defaultTargetQuantity}</td>
              <td />
              <td />
            </tr>
          ))}
        </tbody>
      </table>
      <footer>
        <span>Run: ____________________</span>
        <span>Date: ____________________</span>
        <span>Returned stock: ____________________</span>
      </footer>
    </main>
  );
}
